import { useRef, useCallback, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import DemoTools from "@/components/DemoTools";
import InstallBand from "@/components/InstallBand";

const isDev = import.meta.env.DEV || window.location.hostname.includes("preview");

const LandingPage = ({ isSignedIn, onSignUp, onSignIn, onLaunchApp }: {
  isSignedIn: boolean;
  onSignUp: (plan?: 'monthly' | 'yearly') => void;
  onSignIn: () => void;
  onLaunchApp: () => void;
}) => {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="view active">
      <div className="atmosphere" />
      <div className="landing-wrap">
        <nav className="nav">
          <div className="brand-logo" aria-label="Time Miners" />
          <div className="nav-links">
            <a className="nav-link" onClick={() => scrollTo("how-it-works")}>How it works</a>
            <a className="nav-link" onClick={() => scrollTo("pricing-section")}>Pricing</a>
            {!isSignedIn && <a className="nav-link" onClick={onSignIn}>Sign in</a>}
            <div className="nav-actions">
              {isSignedIn ? (
                <button className="btn btn-primary btn-sm" onClick={onLaunchApp}>Launch app</button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={() => onSignUp()}>Get Started</button>
              )}
            </div>
          </div>
        </nav>

        <section className="hero">
          <div className="hero-eyebrow">
            <span>⛏</span> SCREEN TIME, REIMAGINED
          </div>
          <h1 className="hero-title">
            Kids <span className="accent-green">earn</span> their screen time.<br />
            Parents stay in <span className="accent-gold">control</span>.
          </h1>
          <p className="hero-sub">
            Turn screen time from a source of family conflict into an economy children understand &mdash; and trust. Spend it on the apps you love.
          </p>
          <div className="hero-cta-row">
            <button className="btn btn-primary btn-lg" onClick={() => onSignUp('yearly')}>Start free trial &rarr;</button>
            <button className="btn btn-secondary btn-lg" onClick={() => scrollTo("how-it-works")}>See how it works</button>
          </div>
          <div className="hero-trust">
            <span>✓ 30-day free trial</span>
            <span>✓ Cancel any time</span>
            <span>✓ Sumsub verified</span>
          </div>
          <div className="hero-visual">
            <div className="brand-logo brand-logo-xl" aria-hidden="true" />
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-big">7hrs</div>
              <div className="stat-label">Average daily screen time for kids aged 8&ndash;12</div>
            </div>
            <div className="stat-item">
              <div className="stat-big">73%</div>
              <div className="stat-label">Of parents report daily screen-time conflict</div>
            </div>
            <div className="stat-item">
              <div className="stat-big">£2.99</div>
              <div className="stat-label">Per month, families of any size</div>
            </div>
          </div>
        </section>

        <InstallBand />

        <section className="section" id="how-it-works">
          <div className="section-eyebrow">How it works</div>
          <h2 className="section-title">Simple for kids. <span className="accent-gold">Powerful</span> for parents.</h2>
          <p className="section-sub">No complicated dashboards. Just a system that works &mdash; backed by OS-level enforcement on both iOS and Android.</p>
          <div className="how-grid">
            <div className="how-card">
              <div className="how-step-num">01</div>
              <h3>Kids earn gold through educational apps</h3>
              <p>Parents pick the apps that count as Earn apps &mdash; homework, learning, reading. Every minute spent converts to gold at a rate parents set.</p>
            </div>
            <div className="how-card">
              <div className="how-step-num">02</div>
              <h3>Gold buys time on play apps</h3>
              <p>Kids spend their gold on the games, YouTube, social apps they love. The exchange rate &mdash; parents set that too.</p>
            </div>
            <div className="how-card">
              <div className="how-step-num">03</div>
              <h3>Real enforcement &mdash; not an honour system</h3>
              <p>Time Miners uses Apple Family Controls and Android Device Policy Manager &mdash; the native OS-level APIs that power Apple Screen Time. When gold runs out, apps are blocked at the OS level.</p>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section-eyebrow">The moat</div>
          <h2 className="section-title">Mining Crews &mdash; <span className="accent-green">your kids' friends, mining alongside them</span>.</h2>
          <div className="crew-showcase">
            <div className="crew-showcase-content">
              <div className="chip chip-green">⛏ The Earlys' Crew</div>
              <h3>Your kid's screen time is a solo problem. Their wins should be social.</h3>
              <p>Mining Crews let your kids team up with their school friends &mdash; earning gold together, climbing weekly leaderboards, unlocking shared rewards. The motivation comes from inside the crew. So does the staying power.</p>
              <button className="btn btn-green" onClick={() => onSignUp('yearly')}>Join a Crew &rarr;</button>
            </div>
            <div className="crew-showcase-visual">
              <div className="crew-member-row">
                <div className="crew-member-info">
                  <div className="crew-avatar">🧒</div>
                  <div>
                    <div className="crew-name">Sam</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>🔥 5 day streak</div>
                  </div>
                  <div className="crew-online" />
                </div>
                <div className="crew-gold">+ 1,240</div>
              </div>
              <div className="crew-member-row">
                <div className="crew-member-info">
                  <div className="crew-avatar">👦</div>
                  <div>
                    <div className="crew-name">Tom</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>🔥 8 day streak</div>
                  </div>
                  <div className="crew-online" />
                </div>
                <div className="crew-gold">+ 1,980</div>
              </div>
              <div className="crew-member-row">
                <div className="crew-member-info">
                  <div className="crew-avatar">👧</div>
                  <div>
                    <div className="crew-name">Lily</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>🔥 3 day streak</div>
                  </div>
                </div>
                <div className="crew-gold">+ 820</div>
              </div>
              <div className="crew-member-row">
                <div className="crew-member-info">
                  <div className="crew-avatar">🧒</div>
                  <div>
                    <div className="crew-name">Mia</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>🔥 2 day streak</div>
                  </div>
                </div>
                <div className="crew-gold">+ 320</div>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section-eyebrow">Features</div>
          <h2 className="section-title">Everything a family needs.<br /><span className="accent-gold">Nothing</span> they don't.</h2>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-icon">🛡️</div>
              <h4>Real screen-time enforcement</h4>
              <p>OS-level blocking, not an honour system. Apple Family Controls + Android DPM.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h4>Gold economy</h4>
              <p>Per-app rules. Daily caps. Custom rewards. Kids understand it instantly.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h4>Goals &amp; rewards</h4>
              <p>Save up for the things they want. Real-world rewards. Parent-approved.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👨‍👩‍👧</div>
              <h4>Family accounts</h4>
              <p>Multiple children, co-parent invites, shared visibility, age-tier defaults.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h4>Insights</h4>
              <p>See exactly what your kids spend their time on &mdash; and what they earn from.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⛏️</div>
              <h4>Mining Crew</h4>
              <p>Your kids team up with their friends. Shared goals, leaderboards, banter.</p>
            </div>
          </div>
        </section>

        <section className="pricing-band" id="pricing-section">
          <div className="section-eyebrow">Pricing</div>
          <h2 className="section-title center" style={{ margin: '0 auto 16px' }}>One family. <span className="accent-gold">One price</span>.</h2>
          <p className="section-sub center" style={{ margin: '0 auto 24px' }}>All features, every child, every parent. No upsells.</p>

          <div className="price-card-row">
            <div className="price-card">
              <div className="price-tier">Monthly</div>
              <div className="price-amount">£2.99<span className="per">/mo</span></div>
              <div style={{ height: 18 }} />
              <button className="btn btn-secondary" onClick={() => onSignUp('monthly')}>Start free trial</button>
            </div>
            <div className="price-card featured">
              <div className="price-card-badge">BEST VALUE</div>
              <div className="price-tier">Annual</div>
              <div className="price-amount">£24.99<span className="per">/yr</span></div>
              <div className="price-save">Save 30%</div>
              <button className="btn btn-primary" onClick={() => onSignUp('yearly')}>Start free trial</button>
            </div>
          </div>
          <div className="price-trust">🔐 Secure payments by Stripe · Cancel anytime · 30-day free trial</div>
        </section>

        <footer className="footer">
          <div className="footer-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="mailto:admin@timeminers.app">Support</a>
          </div>
          <div>© 2026 Time Miners Ltd</div>
        </footer>
      </div>
    </div>
  );
};

const Index = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const [showDemoTools, setShowDemoTools] = useState(false);
  const tapCountRef = useRef(0);
  const tapFirstTapTime = useRef(0);

  const handleLogoTap = useCallback((e: React.MouseEvent) => {
    if (!isDev) return;
    e.preventDefault(); e.stopPropagation();
    const now = Date.now();
    if (tapCountRef.current === 0 || now - tapFirstTapTime.current > 3000) {
      tapCountRef.current = 1; tapFirstTapTime.current = now;
    } else { tapCountRef.current += 1; }
    if (tapCountRef.current >= 5) { tapCountRef.current = 0; setShowDemoTools(true); }
  }, []);

  if (!isLoaded) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1020', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(255,200,51,0.4)', borderTopColor: '#FFC833', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (typeof window !== 'undefined' && window.location.hostname.includes("admin")) {
    if (isSignedIn) return <Navigate to="/admin" replace />;
    // Master AdminLogin is the right default landing — it offers password +
    // a link to Clerk-bridged sign-in. The PartnerLogin (Ollie's tier) is
    // reachable from the AdminLogin footer and is no longer the auto-target.
    return <Navigate to="/admin/login" replace />;
  }

  // Signed-in users hitting / should not see the public landing — they get
  // bounced to /hub. This also catches all of Ollie's in-app navigate("/")
  // calls (back buttons, "Go home" from /child/:id profile-not-found, etc.)
  // which otherwise kick the user out of the app onto the marketing site.
  if (isSignedIn) {
    return <Navigate to="/hub" replace />;
  }

  // Both CTAs ("Start free trial" and "Launch app") auth-check first.
  // Signed-in users go straight to /parent (Ollie's hub) — never re-onboarded.
  // Unsigned users go to /sign-up; ProtectedRoute on /parent will then send
  // them through /subscribe → /onboarding → /parent if they're brand new.
  // Both CTAs auth-check first.
  // Signed-in → /hub (Ollie's "EARN TO PLAY" picker — his Index.tsx ported).
  // Not signed in → /sign-up → /subscribe → /onboarding → /hub.
  const handleSignUp = (plan?: 'monthly' | 'yearly') => {
    if (isSignedIn) {
      navigate('/hub');
      return;
    }
    const qs = plan ? `?plan=${plan}` : '';
    navigate(`/sign-up${qs}`);
  };
  const handleSignIn = () => {
    if (isSignedIn) {
      navigate('/hub');
      return;
    }
    navigate('/sign-in');
  };
  const handleLaunchApp = () => navigate('/hub');

  return (
    <>
      <DemoTools open={showDemoTools} onOpenChange={setShowDemoTools} />
      <div onClick={handleLogoTap} style={{ position: 'fixed', top: 0, left: 0, width: 80, height: 64, zIndex: 200 }} />
      <LandingPage
        isSignedIn={!!isSignedIn}
        onSignUp={handleSignUp}
        onSignIn={handleSignIn}
        onLaunchApp={handleLaunchApp}
      />
    </>
  );
};

export default Index;
